import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaTimes,
  FaEdit,
  FaTrash,
  FaCheck,
  FaFilter,
  FaSearch,
} from "react-icons/fa";
import Select from "react-select";
import axiosClient from "../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import { useRef } from "react";
import { showError, showSuccess } from "../../common/Utils";
import { globalErrorHandler } from "../../../utils/ErrorHandler";

const TaskManagement = () => {
  // State management
    const { ToDo } = JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");

  const [tasks, setTasks] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(false);
  const [editingDescriptionMode, setEditingDescriptionMode] = useState(false);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState("");
  const [childTasks, setChildTasks] = useState([]);
  const [loadingChildTasks, setLoadingChildTasks] = useState(false);
  const [showChildTaskModal, setShowChildTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [childTaskFormData, setChildTaskFormData] = useState({
    assignedTo: null,
    subject: "",
    description: "",
    priority: null,
    dueDate: "",
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState(null);
  const [assignedByFilter, setAssignedByFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserId, setCurrentUserId] = useState(0);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingRowData, setEditingRowData] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);

  // Form data
  const [taskFormData, setTaskFormData] = useState({
    assignedTo: null,
    subject: "",
    description: "",
    priority: null,
    dueDate: "",
  });

  // Priority options
  const priorityOptions = [
    { value: 1, label: "High" },
    { value: 2, label: "Medium" },
    { value: 3, label: "Low" },
  ];

  const handleDeleteTaskRow = async (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };

  // 3. ADD this new function to confirm and execute delete
  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setDeletingTaskId(taskToDelete);
    try {
      await axiosClient.delete(`/api/ToDo/${taskToDelete}`);
      toast.success("Task deleted successfully!");
      fetchTasks();

      // Close detail modal if this task is open
      if (selectedTaskForDetail?.id === taskToDelete) {
        handleCloseTaskDetail();
      }

      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    } finally {
      setDeletingTaskId(null);
    }
  };

  // 4. ADD this function to cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  // Inline row editing handlers
  const handleStartEditRow = (task, e) => {
    e.stopPropagation(); // Prevent opening detail modal
    setEditingRowId(task.id);
    setEditingRowData({
      assignedToId: task.assignedToId,
      assignedById: task.assignedById,
      priority: priorityTextToNumber(task.priority),
      status: statusTextToNumber(task.status),
      dueDate: task.dueDate
        ? new Date(task.dueDate).toISOString().split("T")[0]
        : "",
    });
  };

  const handleCancelEditRow = () => {
    setEditingRowId(null);
    setEditingRowData(null);
  };

  const handleAutoSaveField = async (task, field, value) => {
    setSavingRowId(task.id);

    try {
      const updatedData = { ...editingRowData, [field]: value };
      setEditingRowData(updatedData);

      const priorityValue =
        field === "priority"
          ? value
          : updatedData.priority || priorityTextToNumber(task.priority);
      const statusValue =
        field === "status"
          ? value
          : updatedData.status || statusTextToNumber(task.status);
      const assignedById =
        field === "assignedById"
          ? value
          : updatedData.assignedById || task.assignedById;
      const assignedToId =
        field === "assignedTo"
          ? value
          : updatedData.assignedToId || task.assignedToId;
      const dueDateValue =
        field === "dueDate"
          ? new Date(value).toISOString()
          : new Date(updatedData.dueDate || task.dueDate).toISOString();

      const payload = {
        Id: Number(task.id),
        ParentId: Number(task.parentid ?? 0),
        AssignedBy: Number(assignedById),
        AssignedTo: Number(assignedToId),
        Subject: task.subject?.trim() || "Untitled Task",
        Description: task.description?.trim() || "",
        Priority: Number(priorityValue),
        IsRead: Boolean(task.isRead),
        Status: Number(statusValue),
        DueDate: dueDateValue,
        IsDeleted: Boolean(task.isDeleted),
      };

      await axiosClient.put(`/api/ToDo/${task.id}`, payload);
      toast.success("Task updated successfully!", { autoClose: 2000 });
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(error.response?.data?.title || "Failed to update task");
    } finally {
      setSavingRowId(null);
    }
  };

  // Fetch users by corporation
  const fetchUsersByCorporation = async () => {
    try {
      const selectedCorporation = JSON.parse(
        localStorage.getItem("selectedCorporation")
      );
      if (selectedCorporation?.value) {
        const response = await axiosClient.get(
          `/api/User/GetAllUsersByCorporationId/${selectedCorporation.value}`
        );
        if (response && response.data) {
          const users = Array.isArray(response.data) ? response.data : [];
          const activeUsers = users.filter((user) => user.isactive === true);
          setUsersList(activeUsers);
        } else {
          setUsersList([]);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersList([]);
    }
  };

  // ADD THIS NEW FUNCTION to fetch child tasks

  const fetchChildTasks = async (parentTaskId) => {
    setLoadingChildTasks(true);
    try {
      const response = await axiosClient.get(
        `/api/ToDo/allchilditems/${parentTaskId}`
      );
      if (response && response.data) {
        setChildTasks(Array.isArray(response.data) ? response.data : []);
      } else {
        setChildTasks([]);
      }
    } catch (error) {
      console.error("Error fetching child tasks:", error);
      setChildTasks([]);
    } finally {
      setLoadingChildTasks(false);
    }
  };

  // ADD THIS NEW FUNCTION to handle opening child task modal

  const handleOpenChildTaskModal = () => {
    setShowChildTaskModal(true);
    setChildTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
  };

  // ADD THIS NEW FUNCTION to close child task modal

  const handleCloseChildTaskModal = () => {
    setShowChildTaskModal(false);
    setChildTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
  };

  // ADD THIS NEW FUNCTION to handle child task form changes

  const handleChildTaskFormChange = (field, value) => {
    setChildTaskFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ADD THIS NEW FUNCTION to create child task

  const handleCreateChildTask = async () => {
    if (
      !childTaskFormData.assignedTo ||
      !childTaskFormData.subject ||
      !childTaskFormData.priority
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUpdatingTask(true);
    try {
      let dueDateString;
      if (childTaskFormData.dueDate) {
        // Get current time components
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();

        // Append current time to the selected date
        dueDateString = `${childTaskFormData.dueDate}T${String(hours).padStart(
          2,
          "0"
        )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`;
      } else {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 7);
        const year = dueDate.getFullYear();
        const month = String(dueDate.getMonth() + 1).padStart(2, "0");
        const day = String(dueDate.getDate()).padStart(2, "0");
        const hours = String(dueDate.getHours()).padStart(2, "0");
        const minutes = String(dueDate.getMinutes()).padStart(2, "0");
        const seconds = String(dueDate.getSeconds()).padStart(2, "0");
        dueDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      const payload = {
        id: 0,
        parentid: selectedTaskForDetail.id,
        assignedBy: currentUserId,
        assignedTo: childTaskFormData.assignedTo.value,
        subject: childTaskFormData.subject,
        description: childTaskFormData.description || "",
        priority: childTaskFormData.priority.value,
        isRead: false,
        status: 1,
        dueDate: dueDateString,
        isDeleted: false,
      };

      console.log("Child task payload being sent:", payload);
      await axiosClient.post("/api/ToDo", payload);
      toast.success("Child task created successfully!");
      handleCloseChildTaskModal();
      fetchChildTasks(selectedTaskForDetail.id);
    } catch (error) {
      console.error("Error creating child task:", error);
      toast.error("Failed to create child task");
    } finally {
      setUpdatingTask(false);
    }
  };

  // Add this function to handle editing description
  const handleStartEditDescription = () => {
    setEditingDescriptionMode(true);
    setEditingDescriptionValue(selectedTaskForDetail.description || "");
  };

  // Add this function to save description changes
  const handleSaveDescription = async () => {
    if (!editingDescriptionValue.trim()) {
      toast.error("Description cannot be empty");
      return;
    }

    setUpdatingTask(true);
    try {
      const updatedTask = { ...selectedTaskForDetail };
      updatedTask.description = editingDescriptionValue.trim();

      const assignedById = Number(
        updatedTask.assignedById || updatedTask.assignedBy || currentUserId || 0
      );
      const assignedToId = Number(
        updatedTask.assignedToId || updatedTask.assignedTo || 0
      );

      const priorityValue = priorityTextToNumber(
        updatedTask.priority !== null &&
          updatedTask.priority !== undefined &&
          updatedTask.priority !== ""
          ? updatedTask.priority
          : selectedTaskForDetail.priority ?? 2
      );

      const statusValue = statusTextToNumber(
        updatedTask.status !== null &&
          updatedTask.status !== undefined &&
          updatedTask.status !== ""
          ? updatedTask.status
          : selectedTaskForDetail.status
      );

      const payload = {
        Id: Number(updatedTask.id ?? 0),
        ParentId: Number(updatedTask.parentid ?? 0),
        AssignedBy: assignedById,
        AssignedTo: assignedToId,
        Subject:
          updatedTask.subject?.trim() ||
          selectedTaskForDetail.subject ||
          "Untitled Task",
        Description: updatedTask.description,
        Priority: isNaN(priorityValue) ? 2 : priorityValue,
        IsRead: Boolean(updatedTask.isRead),
        Status: statusValue,
        DueDate: updatedTask.dueDate
          ? new Date(updatedTask.dueDate).toISOString()
          : new Date().toISOString(),
        IsDeleted: Boolean(updatedTask.isDeleted),
      };

      // Optimistic update - update UI immediately
      setSelectedTaskForDetail(updatedTask);
      setEditingDescriptionMode(false);
      toast.success("Description updated successfully!");

      // Then save to server
      await axiosClient.put(`/api/ToDo/${payload.Id}`, payload);

      // Fetch tasks to sync with backend
      fetchTasks();
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error(
        error.response?.data?.title || "Failed to update description"
      );
      // Revert on error by re-fetching
      fetchComments(selectedTaskForDetail.id);
    } finally {
      setUpdatingTask(false);
    }
  };

  // Fetch comments for a task
  const fetchComments = async (taskId) => {
    setLoadingComments(true);
    try {
      const response = await axiosClient.get(`/api/ToDo/allcomments/${taskId}`);
      if (response && response.data) {
        const commentsList = Array.isArray(response.data) ? response.data : [];
        // Reverse to show latest comments first
        setComments(commentsList.reverse());
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };
  // Open task detail modal
  const handleOpenTaskDetail = (task) => {
    setSelectedTaskForDetail(task);
    setShowTaskDetailModal(true);
    setNewComment("");
    setIsEditingDetail(false);
    fetchComments(task.id);
    fetchChildTasks(task.id); // ADD THIS LINE to fetch child tasks
  };

  // Close task detail modal

  const handleCloseTaskDetail = () => {
    setShowTaskDetailModal(false);
    setSelectedTaskForDetail(null);
    setComments([]);
    setNewComment("");
    setIsEditingDetail(false);
    setChildTasks([]); // ADD THIS LINE to clear child tasks
  };

  const handleUpdateTaskFromDetail = async (field, value) => {
    setUpdatingTask(true);
    try {
      const updatedTask = { ...selectedTaskForDetail };

      // Dynamically update field
      if (field === "assignedTo") {
        updatedTask.assignedToId = value;
      } else if (field === "assignedBy") {
        updatedTask.assignedById = value;
      } else if (field === "priority") {
        updatedTask.priority = value;
      } else if (field === "status") {
        updatedTask.status = statusNumberToText(value);
      } else if (field === "dueDate") {
        updatedTask.dueDate = new Date(value).toISOString();
      }

      const assignedById = Number(
        updatedTask.assignedById || updatedTask.assignedBy || currentUserId || 0
      );
      const assignedToId = Number(
        updatedTask.assignedToId || updatedTask.assignedTo || 0
      );

      const priorityValue = priorityTextToNumber(
        updatedTask.priority !== null &&
          updatedTask.priority !== undefined &&
          updatedTask.priority !== ""
          ? updatedTask.priority
          : selectedTaskForDetail.priority ?? 2
      );

      // Convert status text to number for API
      const statusValue = statusTextToNumber(
        updatedTask.status !== null &&
          updatedTask.status !== undefined &&
          updatedTask.status !== ""
          ? updatedTask.status
          : selectedTaskForDetail.status
      );

      const payload = {
        Id: Number(updatedTask.id ?? 0),
        ParentId: Number(updatedTask.parentid ?? 0),
        AssignedBy: assignedById,
        AssignedTo: assignedToId,
        Subject:
          updatedTask.subject?.trim() ||
          selectedTaskForDetail.subject ||
          "Untitled Task",
        Description:
          updatedTask.description?.trim() ||
          selectedTaskForDetail.description ||
          "No description provided",
        Priority: isNaN(priorityValue) ? 2 : priorityValue,
        IsRead: Boolean(updatedTask.isRead),
        Status: statusValue, // Send as number
        DueDate: updatedTask.dueDate
          ? new Date(updatedTask.dueDate).toISOString()
          : new Date().toISOString(),
        IsDeleted: Boolean(updatedTask.isDeleted),
      };

      console.log("🧾 Safe Payload:", payload);

      await axiosClient.put(`/api/ToDo/${payload.Id}`, payload);
      toast.success("Task updated successfully!");

      updatedTask.assignedById = assignedById;
      updatedTask.assignedToId = assignedToId;
      updatedTask.priority = payload.Priority;
      updatedTask.status = statusNumberToText(payload.Status); // Store as text
      setSelectedTaskForDetail(updatedTask);

      fetchTasks();
    } catch (error) {
      console.error("❌ Error updating task:", error);
      console.error("Response:", error.response?.data);
      toast.error(error.response?.data?.title || "Failed to update task");
    } finally {
      setUpdatingTask(false);
    }
  };

  // Submit new comment
  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const payload = {
        id: 0,
        todoid: selectedTaskForDetail.id,
        userid: 0,
        comments: newComment.trim(),
        createdon: new Date().toISOString(),
        updatedon: new Date().toISOString(),
      };

      await axiosClient.post("/api/ToDo/Comments", payload);
      toast.success("Comment added successfully!");

      // Clear input immediately
      setNewComment("");

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      // Add optimistic update - add comment immediately before fetching
      const newCommentObj = {
        id: Date.now(),
        todoid: selectedTaskForDetail.id,
        userid: 0,
        comments: payload.comments,
        createdon: payload.createdon,
        updatedon: payload.updatedon,
      };

      setComments((prevComments) => [newCommentObj, ...prevComments]);

      // Then fetch fresh comments from server
      fetchComments(selectedTaskForDetail.id);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Get commenter name
  const getCommenterName = (comment) => {
    try {
      const user = usersList.find((u) => u.id === comment.userid);
      return user ? `${user.first_name} ${user.last_name}` : "Unknown User";
    } catch (error) {
      return "Unknown User";
    }
  };

  // Fetch tasks based on filters
  const fetchTasks = async () => {
    setLoading(true);
    try {
      let response;

      // Priority: Assigned By filter
      if (assignedByFilter) {
        const userId =
          assignedByFilter.value === "me" ? 0 : assignedByFilter.value;
        response = await axiosClient.get(`/api/ToDo/assigned-by/${userId}`);

        // If also filtering by Assigned To, apply client-side filter
        if (assignedToFilter && response && response.data) {
          const allTasks = Array.isArray(response.data) ? response.data : [];
          const targetUserId =
            assignedToFilter.value === "me"
              ? currentUserId
              : assignedToFilter.value;
          const filtered = allTasks.filter(
            (task) => task.assignedToId === targetUserId
          );
          setTasks(filtered);
          setLoading(false);
          return;
        }
      }
      // Then: Assigned To filter
      else if (assignedToFilter) {
        const userId =
          assignedToFilter.value === "me"
            ? currentUserId
            : assignedToFilter.value;
        // If user selected a specific user, use /all endpoint and filter
        if (assignedToFilter.value !== "me") {
          response = await axiosClient.get(`/api/ToDo/all`);
          if (response && response.data) {
            const allTasks = Array.isArray(response.data) ? response.data : [];
            const filtered = allTasks.filter(
              (task) => task.assignedToId === assignedToFilter.value
            );
            setTasks(filtered);
            setLoading(false);
            return;
          }
        } else {
          response = await axiosClient.get(`/api/ToDo/assigned-to`);
        }
      }
      // Default: Show my tasks (assigned to me)
      else {
        response = await axiosClient.get(`/api/ToDo/assigned-to`);
      }

      if (response && response.data) {
        setTasks(Array.isArray(response.data) ? response.data : []);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    // Get current user ID from localStorage or set default
    const userData = JSON.parse(localStorage.getItem("userData"));
    const userId = userData?.id || 0;
    setCurrentUserId(userId);

    fetchUsersByCorporation();
  }, []);

  // Fetch tasks when filters change
  useEffect(() => {
    if (currentUserId !== null) {
      fetchTasks();
    }
  }, [assignedToFilter, assignedByFilter, currentUserId]);

  // Get team member name
  const getTeamMemberName = (task) => {
    try {
      if (task.assignedTo && typeof task.assignedTo === "string") {
        return task.assignedTo;
      }
      const user = usersList.find((user) => user.id === task.assignedToId);
      return user
        ? `${user.first_name} ${user.last_name}`
        : task.assignedTo || "Unknown Member";
    } catch (error) {
      return "Unknown Member";
    }
  };

  // Get assigned by name
  const getAssignedByName = (task) => {
    try {
      if (task.assignedBy && typeof task.assignedBy === "string") {
        return task.assignedBy;
      }
      const user = usersList.find((user) => user.id === task.assignedById);
      return user ? `${user.first_name} ${user.last_name}` : "Unknown";
    } catch (error) {
      return "Unknown";
    }
  };

  const descriptionRef = useRef(null);

  // Get priority info
  const getPriorityInfo = (priority) => {
    const priorityNum = priorityTextToNumber(priority);
    switch (priorityNum) {
      case 1:
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          text: "High",
        };
      case 2:
        return {
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          text: "Medium",
        };
      case 3:
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          text: "Low",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          text: "Medium",
        };
    }
  };

  // 1. UPDATE getStatusBadge function (replace old one)
  const getStatusBadge = (status) => {
    const statusNum = statusTextToNumber(status);

    switch (statusNum) {
      case 1:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-purple-100 text-purple-800 border-purple-200">
            New
          </span>
        );
      case 2:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
            In Progress
          </span>
        );
      case 3:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200">
            Closed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-200">
            Unknown
          </span>
        );
    }
  };

  // Filter tasks (client-side filters after API fetch)
  const getFilteredTasks = () => {
    let filtered = [...tasks];

    // 2. UPDATE getFilteredTasks function - Replace status filter logic
    if (statusFilter !== "all") {
      filtered = filtered.filter((task) => {
        const statusNum = statusTextToNumber(task.status);
        if (statusFilter === "new") {
          return statusNum === 1;
        } else if (statusFilter === "inprogress") {
          return statusNum === 2;
        } else if (statusFilter === "closed") {
          return statusNum === 3;
        }
        return true;
      });
    }

    // Priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(
        (task) => parseInt(task.priority) === parseInt(priorityFilter)
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.subject?.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          task.id?.toString().includes(query)
      );
    }

    return filtered;
  };

  // Clear all filters
  const handleClearFilters = () => {
    setAssignedToFilter(null);
    setAssignedByFilter(null);
    setStatusFilter("all");
    setPriorityFilter("all");
    setSearchQuery("");
  };

  // Modal handlers
  const handleOpenCreateModal = () => {
    setModalMode("create");
    setSelectedTask(null);
    setTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
    setShowTaskModal(true);
  };

  const handleOpenEditModal = (task) => {
    setModalMode("edit");
    setSelectedTask(task);

    const userOption = usersList.find((u) => u.id === task.assignedToId);
    const priorityNum = priorityTextToNumber(task.priority);
    const priorityOption = priorityOptions.find((p) => p.value === priorityNum);

    setTaskFormData({
      assignedTo: userOption
        ? {
            value: userOption.id,
            label: `${userOption.first_name} ${userOption.last_name}`,
          }
        : null,
      subject: task.subject || "",
      description: task.description || "",
      priority: priorityOption || null,
      dueDate: task.dueDate
        ? new Date(task.dueDate).toISOString().split("T")[0]
        : "",
    });
    setShowTaskModal(true);
  };

  const handleCloseModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    setTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
  };

  // 1. ADD these priority mapping functions at the top
  const priorityTextToNumber = (priorityInput) => {
    if (!priorityInput) return 2; // Default to Medium

    // Handle both text and number inputs
    const priorityMap = {
      High: 1,
      Medium: 2,
      Low: 3,
      high: 1,
      medium: 2,
      low: 3,
      1: 1,
      2: 2,
      3: 3,
      1: 1,
      2: 2,
      3: 3,
    };

    return priorityMap[priorityInput] || 2; // Default to Medium if unknown
  };

  const priorityNumberToText = (priorityNumber) => {
    const priorityMap = {
      1: "High",
      2: "Medium",
      3: "Low",
    };
    return priorityMap[priorityNumber] || "Medium";
  };

  // 1. ADD this status mapping function near the top of your component
  const statusTextToNumber = (statusText) => {
    const statusMap = {
      New: 1,
      Inprogress: 2,
      "In Progress": 2,
      Closed: 3,
      1: 1,
      2: 2,
      3: 3,
    };
    return statusMap[statusText] || 0;
  };

  const statusNumberToText = (statusNumber) => {
    const statusMap = {
      1: "New",
      2: "In Progress",
      3: "Closed",
      0: "New",
    };
    return statusMap[statusNumber] || "Unknown";
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setTaskFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Create task
  const handleCreateTask = async () => {
    if (
      !taskFormData.assignedTo ||
      !taskFormData.subject ||
      !taskFormData.priority
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      let dueDateString;
      if (taskFormData.dueDate) {
        // Get current time components
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();

        // Append current time to the selected date
        dueDateString = `${taskFormData.dueDate}T${String(hours).padStart(
          2,
          "0"
        )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`;
      } else {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 7);
        const year = dueDate.getFullYear();
        const month = String(dueDate.getMonth() + 1).padStart(2, "0");
        const day = String(dueDate.getDate()).padStart(2, "0");
        const hours = String(dueDate.getHours()).padStart(2, "0");
        const minutes = String(dueDate.getMinutes()).padStart(2, "0");
        const seconds = String(dueDate.getSeconds()).padStart(2, "0");
        dueDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      const payload = {
        id: 0,
        parentid: 0,
        assignedBy: currentUserId,
        assignedTo: taskFormData.assignedTo.value,
        subject: taskFormData.subject,
        description: taskFormData.description || "",
        priority: taskFormData.priority.value,
        isRead: false,
        status: 1,
        dueDate: dueDateString,
        isDeleted: false,
      };

      console.log("Payload being sent:", payload);
      await axiosClient.post("/api/ToDo", payload);
      toast.success("Task created successfully!");
      handleCloseModal();
      fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setLoading(false);
    }
  };
  const textareaRef = useRef(null);

  // Update task
  const handleUpdateTask = async () => {
    if (
      !taskFormData.assignedTo ||
      !taskFormData.subject ||
      !taskFormData.priority
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const dueDate = taskFormData.dueDate
        ? new Date(taskFormData.dueDate)
        : new Date(selectedTask.dueDate);

      const payload = {
        id: selectedTask.id,
        parentid: selectedTask.parentid || 0, // Added parentid
        assignedBy: selectedTask.assignedBy,
        assignedTo: taskFormData.assignedTo.value,
        subject: taskFormData.subject,
        description: taskFormData.description || "",
        priority: taskFormData.priority.value,
        isRead: selectedTask.isRead,
        status: selectedTask.status,
        dueDate: dueDate.toISOString(),
        isDeleted: false,
      };

      await axiosClient.put(`/api/ToDo/${selectedTask.id}`, payload);
      toast.success("Task updated successfully!");
      handleCloseModal();
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      await axiosClient.delete(`/api/ToDo/${taskId}`);
      toast.success("Task deleted successfully!");
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  // Mark task as complete
  const handleMarkComplete = async (taskId) => {
    try {
      await axiosClient.put(`/api/ToDo/${taskId}/mark-closed`);
      toast.success("Task marked as complete!");
      fetchTasks();
    } catch (error) {
      console.error("Error marking task complete:", error);
      toast.error("Failed to mark task as complete");
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const filteredTasks = getFilteredTasks();

  // User options for dropdowns
  const userOptions = [
    { value: "me", label: "Me" },
    ...usersList.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    })),
  ];

  return (
    <>
      <style>{`
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      .animate-fade-in {
        animation: fadeIn 0.2s ease-in-out;
      }

      .animate-scale-in {
        animation: scaleIn 0.3s ease-out;
      }
    `}</style>

      <div className="w-full">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Task Management
                </h1>
                <p className="text-gray-600 mt-0.5 text-sm md:text-base">
                  Manage and track all your tasks in one place
                </p>
              </div>
              <button
                onClick={handleOpenCreateModal}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-sm text-sm md:text-base"
              >
                <FaPlus />
                Create Task
              </button>
            </div>

            {/* Filters */}
            <div className="space-y-2 md:space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {/* Search */}
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by subject, description or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  />
                </div>

                {/* Assigned To Filter */}
                <div>
                  <Select
                    value={assignedToFilter}
                    onChange={setAssignedToFilter}
                    options={userOptions}
                    placeholder="Assigned To"
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "42px",
                        fontSize: "14px",
                      }),
                    }}
                  />
                </div>

                {/* Assigned By Filter */}
                <div>
                  <Select
                    value={assignedByFilter}
                    onChange={setAssignedByFilter}
                    options={userOptions}
                    placeholder="Assigned By"
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "42px",
                        fontSize: "14px",
                      }),
                    }}
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="inprogress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>

                {/* Priority Filter */}
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                >
                  <option value="all">All Priorities</option>
                  <option value="1">High</option>
                  <option value="2">Medium</option>
                  <option value="3">Low</option>
                </select>
              </div>

              {/* Filter Info and Clear Button */}
              <div className="flex items-center justify-between text-sm">
                <div className="text-gray-600">
                  Showing {filteredTasks.length} of {tasks.length} tasks
                  {!assignedToFilter && !assignedByFilter && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      My Tasks
                    </span>
                  )}
                </div>
                {(assignedToFilter ||
                  assignedByFilter ||
                  statusFilter !== "all" ||
                  priorityFilter !== "all" ||
                  searchQuery) && (
                  <button
                    onClick={handleClearFilters}
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <FaTimes className="text-xs" />
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tasks Table */}
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading tasks...</span>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium">No tasks found</p>
                <p className="text-sm mt-2">
                  {tasks.length === 0
                    ? "Create a new task to get started"
                    : "Try adjusting your filters"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Task ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Assigned By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Dependent Tasks
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
  {ToDo?.canDelete ? "Actions" : "Edit"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTasks.map((task) => {
                      const isEditing = editingRowId === task.id;
                      const isSaving = savingRowId === task.id;

                      return (
                        <React.Fragment key={task.id}>
                          <tr
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() =>
                              !isEditing && handleOpenTaskDetail(task)
                            }
                          >
                            {/* Task ID */}
                            <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                              {task.id}
                            </td>

                            {/* Subject */}
                            <td className="px-6 py-4">
                              <p className="text-sm font-medium text-gray-900">
                                {task.subject}
                              </p>
                            </td>

                            {/* Assigned To */}
                            <td
                              className="px-6 py-4"
                              onClick={(e) => isEditing && e.stopPropagation()}
                            >
                              {isEditing ? (
                                <Select
                                  value={
                                    usersList.find(
                                      (u) =>
                                        u.id ===
                                        (editingRowData.assignedToId ||
                                          task.assignedToId)
                                    )
                                      ? {
                                          value:
                                            editingRowData.assignedToId ||
                                            task.assignedToId,
                                          label: getTeamMemberName({
                                            ...task,
                                            assignedToId:
                                              editingRowData.assignedToId ||
                                              task.assignedToId,
                                          }),
                                        }
                                      : null
                                  }
                                  onChange={(selected) => {
                                    if (selected) {
                                      handleAutoSaveField(
                                        task,
                                        "assignedTo",
                                        selected.value
                                      );
                                    }
                                  }}
                                  options={usersList.map((user) => ({
                                    value: user.id,
                                    label: `${user.first_name} ${user.last_name}`,
                                  }))}
                                  isSearchable
                                  isDisabled={isSaving}
                                  menuPortalTarget={document.body}
                                  menuPosition="absolute"
                                  menuPlacement="auto"
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      minHeight: "36px",
                                      fontSize: "14px",
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              ) : (
                                <span className="text-sm text-gray-700">
                                  {getTeamMemberName(task)}
                                </span>
                              )}
                            </td>

                            {/* Assigned By */}
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-700">
                                {getAssignedByName(task)}
                              </span>
                            </td>

                            {/* Priority */}
                            <td
                              className="px-6 py-4"
                              onClick={(e) => isEditing && e.stopPropagation()}
                            >
                              {isEditing ? (
                                <Select
                                  value={priorityOptions.find(
                                    (p) =>
                                      p.value ===
                                      (editingRowData.priority ||
                                        priorityTextToNumber(task.priority))
                                  )}
                                  onChange={(selected) => {
                                    if (selected) {
                                      handleAutoSaveField(
                                        task,
                                        "priority",
                                        selected.value
                                      );
                                    }
                                  }}
                                  options={priorityOptions}
                                  isSearchable
                                  isDisabled={isSaving}
                                  menuPortalTarget={document.body}
                                  menuPosition="absolute"
                                  menuPlacement="auto"
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      minHeight: "36px",
                                      fontSize: "14px",
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                    getPriorityInfo(task.priority).color
                                  }`}
                                >
                                  {getPriorityInfo(task.priority).text}
                                </span>
                              )}
                            </td>

                            {/* Status */}
                            <td
                              className="px-6 py-4"
                              onClick={(e) => isEditing && e.stopPropagation()}
                            >
                              {isEditing ? (
                                <select
                                  value={
                                    editingRowData.status ||
                                    statusTextToNumber(task.status)
                                  }
                                  onChange={(e) =>
                                    handleAutoSaveField(
                                      task,
                                      "status",
                                      Number(e.target.value)
                                    )
                                  }
                                  disabled={isSaving}
                                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                >
                                  <option value="1">New</option>
                                  <option value="2">In Progress</option>
                                  <option value="3">Closed</option>
                                </select>
                              ) : (
                                getStatusBadge(task.status)
                              )}
                            </td>

                            {/* Created Date */}
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {task.createdOn
                                ? new Date(task.createdOn).toLocaleDateString()
                                : "N/A"}
                            </td>

                            {/* Due Date */}
                            <td
                              className="px-6 py-4"
                              onClick={(e) => isEditing && e.stopPropagation()}
                            >
                              {isEditing ? (
                                <input
                                  type="date"
                                  value={
                                    editingRowData.dueDate ||
                                    (task.dueDate
                                      ? new Date(task.dueDate)
                                          .toISOString()
                                          .split("T")[0]
                                      : "")
                                  }
                                  onChange={(e) =>
                                    handleAutoSaveField(
                                      task,
                                      "dueDate",
                                      e.target.value
                                    )
                                  }
                                  disabled={isSaving}
                                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                              ) : (
                                <span className="text-sm text-gray-700">
                                  {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </td>

                            {/* Dependent Tasks */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {task.dependentTaskCount > 0 ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                    {task.dependentTaskCount}{" "}
                                    {task.dependentTaskCount === 1
                                      ? "Task"
                                      : "Tasks"}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                    No Dependencies
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Actions Column */}
                           {/* Actions Column */}
<td
  className="px-6 py-4"
  onClick={(e) => e.stopPropagation()}
>
  <div className="flex items-center gap-2">
    {isSaving ? (
      <div className="flex items-center gap-2 text-blue-600">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-xs">Saving...</span>
      </div>
    ) : isEditing ? (
      <button
        onClick={() => handleCancelEditRow()}
        className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
        title="Done Editing"
      >
        <FaCheck className="text-sm" />
      </button>
    ) : (
      <>
        <button
          onClick={(e) => handleStartEditRow(task, e)}
          className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors"
          title="Edit Task"
        >
          <FaEdit className="text-sm" />
        </button>
        {ToDo?.canDelete && (
          <button
            onClick={() => handleDeleteTaskRow(task.id)}
            className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
            title="Delete Task"
          >
            <FaTrash className="text-sm" />
          </button>
        )}
      </>
    )}
  </div>
</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4 animate-fade-in">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full transform transition-all animate-scale-in">
            {/* Modal Header */}
            <div className="flex items-start gap-3 p-6 border-b border-gray-200 bg-red-50">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <FaTrash className="text-red-600 text-lg" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900">
                  Delete Task
                  </h2>
                <p className="text-sm text-gray-600 mt-1">
                  This action cannot be undone
                </p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to delete this task? All associated data
                including comments and sub-tasks will be permanently removed.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={handleCancelDelete}
                disabled={deletingTaskId !== null}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingTaskId !== null}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {deletingTaskId !== null ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash className="text-sm" />
                    Delete Task
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {modalMode === "create" ? "Create New Task" : "Edit Task"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To <span className="text-red-500">*</span>
                </label>
                <Select
                  value={taskFormData.assignedTo}
                  onChange={(selected) =>
                    handleFormChange("assignedTo", selected)
                  }
                  options={usersList.map((user) => ({
                    value: user.id,
                    label: `${user.first_name} ${user.last_name} (${user.email})`,
                  }))}
                  placeholder="Select a team member"
                  isSearchable
                  isClearable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={taskFormData.subject}
                  onChange={(e) => handleFormChange("subject", e.target.value)}
                  placeholder="Enter task subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  placeholder="Enter task description"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-vertical"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority <span className="text-red-500">*</span>
                </label>
                <Select
                  value={taskFormData.priority}
                  onChange={(selected) =>
                    handleFormChange("priority", selected)
                  }
                  options={priorityOptions}
                  placeholder="Select priority level"
                  isClearable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={taskFormData.dueDate}
                  onChange={(e) => handleFormChange("dueDate", e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseModal}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={
                  modalMode === "create" ? handleCreateTask : handleUpdateTask
                }
                disabled={
                  loading ||
                  !taskFormData.assignedTo ||
                  !taskFormData.subject ||
                  !taskFormData.priority
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {loading
                  ? "Saving..."
                  : modalMode === "create"
                  ? "Create Task"
                  : "Update Task"}
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Task Detail Modal with Comments */}
      {/* Task Detail Modal with Comments */}
    {/* Task Detail Modal with Comments */}
      {showTaskDetailModal && selectedTaskForDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden">
            {/* Modal Header - COMPACT */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-gray-900 truncate">
                  {selectedTaskForDetail.subject}
                </h2>

                <div className="flex items-center flex-wrap gap-3 text-xs text-gray-600 mt-1">
                  <span className="font-medium">ID: {selectedTaskForDetail.id}</span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span>
                    Created:{" "}
                    {new Date(selectedTaskForDetail.createdOn).toLocaleDateString()}
                  </span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>

                  {/* Assigned By Inline */}
                  <span className="flex items-center gap-1">
                    <span className="uppercase tracking-wide font-semibold text-gray-600">
                      Assigned By:
                    </span>
                    <span className="text-gray-900 font-medium mb-0">
                      {getAssignedByName(selectedTaskForDetail)}
                    </span>
                  </span>
                </div>
              </div>

              <button
                onClick={handleCloseTaskDetail}
                className="p-1.5 rounded-lg hover:bg-white/80 transition-colors ml-3 flex-shrink-0"
              >
                <FaTimes className="text-gray-600 text-lg" />
              </button>
            </div>


            {/* Modal Body - COMPACT SCROLLABLE */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Task Details Grid - COMPACT */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
                  {/* Assigned To */}
                  <div className="flex items-baseline gap-1.5">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Assigned To:
                    </label>
                    {isEditingDetail ? (
                      <div className="min-w-[140px]">
                        <Select
                          value={
                                       usersList.find((u) => u.id === selectedTaskForDetail.assignedToId)
                              ? {
                                  value: selectedTaskForDetail.assignedToId,
                                  label: getTeamMemberName(selectedTaskForDetail),
                                }
                              : null
                          }
                          onChange={(selected) => {
                            if (selected) {
                             handleUpdateTaskFromDetail("assignedTo", selected.value);
                            }
                          }}
                          options={usersList.map((user) => ({
                            value: user.id,
                            label: `${user.first_name} ${user.last_name}`,
                          }))}
                          placeholder="Select user"
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="absolute"
                          menuPlacement="auto"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "28px",
                              fontSize: "13px",
                              borderColor: "#d1d5db",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          isDisabled={updatingTask}
                        />
                      </div>
                    ) : (
                      <p className="text-[13px] text-gray-900 font-medium mb-0">
                        {getTeamMemberName(selectedTaskForDetail)}
                      </p>
                    )}
                  </div>

                  {/* Priority */}
                  <div className="flex items-baseline gap-1.5">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Priority:
                    </label>
                    {isEditingDetail ? (
                      <div className="min-w-[120px]">
                        <Select
                          value={priorityOptions.find(
                            (p) =>
                              p.value ===
                              priorityTextToNumber(selectedTaskForDetail.priority)
                          )}
                          onChange={(selected) => {
                            if (selected) {
                              handleUpdateTaskFromDetail("priority", selected.value);
                            }
                          }}
                          options={priorityOptions}
                          menuPortalTarget={document.body}
                          menuPosition="absolute"
                          menuPlacement="auto"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "28px",
                              fontSize: "13px",
                              borderColor: "#d1d5db",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          isDisabled={updatingTask}
                        />
                      </div>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                          getPriorityInfo(selectedTaskForDetail.priority).color
                        }`}
                      >
                        {getPriorityInfo(selectedTaskForDetail.priority).text}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-baseline gap-1.5">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Status:
                    </label>
                    {isEditingDetail ? (
                      <select
                        value={selectedTaskForDetail.status}
                        onChange={(e) =>
                          handleUpdateTaskFromDetail("status", e.target.value)
                        }
                        className="px-2.5 py-1 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        disabled={updatingTask}
                      >
                        <option value="1">New</option>
                        <option value="2">In Progress</option>
                        <option value="3">Closed</option>
                      </select>
                    ) : (
                      getStatusBadge(selectedTaskForDetail.status)
                    )}
                  </div>

                  {/* Due Date */}
                  <div className="flex items-baseline gap-1.5">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Due Date:
                    </label>
                    {isEditingDetail ? (
                      <input
                        type="date"
                        value={
                          selectedTaskForDetail.dueDate
                            ? new Date(selectedTaskForDetail.dueDate)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const updatedTask = { ...selectedTaskForDetail };
                          updatedTask.dueDate = new Date(
                            e.target.value
                          ).toISOString();
                          setSelectedTaskForDetail(updatedTask);
                        }}
                        className="px-2.5 py-1 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        disabled={updatingTask}
                      />
                    ) : (
                      <p className="text-[13px] text-gray-900 font-medium mb-0">
                        {new Date(selectedTaskForDetail.dueDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>

  {/* Right section: Edit Icon - Only visible when NOT editing */}
                  {!isEditingDetail && (
                    <button
                      onClick={() => setIsEditingDetail(true)}
                      className="text-gray-500 hover:text-blue-600 transition-colors ml-auto"
                    >
                      <FaEdit className="text-sm" />
                    </button>
                  )}
                </div>

{/* Edit Mode Controls - COMPACT */}
                {isEditingDetail && (
                  <div className="flex justify-end gap-2 pt-2.5 mt-2.5 border-t">
                    <button
                      onClick={() => setIsEditingDetail(false)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      disabled={updatingTask}
                    >
                      Done Editing
                    </button>
                  </div>
                )}

          {/* Loading Indicator - COMPACT */}
                {updatingTask && (
                  <div className="flex items-center justify-center py-2 bg-blue-50 rounded-lg mt-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-xs font-medium text-blue-600">
                      Updating...
                    </span>
                  </div>
                )}
              </div>

              {/* Description Section - COMPACT */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-600 rounded"></span>
                  Description
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  {editingDescriptionMode ? (
                    <div className="space-y-2">
                      {/* Auto-growing Textarea */}
                      <textarea
                        ref={descriptionRef}
                        value={editingDescriptionValue}
                        onChange={(e) => {
                          setEditingDescriptionValue(e.target.value);
                          if (descriptionRef.current) {
                            descriptionRef.current.style.height = "auto"; // reset height
                            descriptionRef.current.style.height =
                              descriptionRef.current.scrollHeight + "px"; // set to content height
                          }
                        }}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm resize-none overflow-hidden"
                        disabled={updatingTask}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingDescriptionMode(false)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          disabled={updatingTask}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveDescription}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                          disabled={updatingTask}
                        >
                          {updatingTask && (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer hover:text-blue-600 flex items-start gap-2 group"
                      onClick={handleStartEditDescription}
                    >
                      <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1 mb-0">
                        {selectedTaskForDetail.description || "No description provided"}
                      </p>
                      <FaEdit className="text-xs text-gray-400 group-hover:text-blue-600 mt-0.5 flex-shrink-0" />
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section - COMPACT */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-600 rounded"></span>
                  Comments ({comments.length})
                </h3>

                {/* Comments List - COMPACT */}
                <div className="space-y-2.5 mb-3 max-h-48 overflow-y-auto">
                  {loadingComments ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">
                        Loading comments...
                      </span>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 mt-3">
                        No comments yet. Be the first to comment!
                      </p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id || Math.random()}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3 animate-fade-in"
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                            {getCommenterName(comment).charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 mb-1">
                              <p className="text-xs font-semibold text-gray-900 truncate">
                                {getCommenterName(comment)}
                              </p>
                              <p className="text-xs text-gray-500 flex-shrink-0">
                                {new Date(
                                  comment.createdon
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words -mb-1">
                              {comment.comments}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )
                  }
                </div>

                <div className="border-t pt-3 mt-3 flex flex-wrap items-end gap-3">
                  {/* Comment Input + Post Button */}
                  <div className="flex flex-1 items-end gap-2">
                    <textarea
                      ref={textareaRef}
                      value={newComment}
                      onChange={(e) => {
                        setNewComment(e.target.value);
                        if (textareaRef.current) {
                          textareaRef.current.style.height = "auto";
                          textareaRef.current.style.height =
                            textareaRef.current.scrollHeight + "px";
                        }
                      }}
                      placeholder="Write a comment..."
                      rows={1}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none text-sm overflow-hidden"
                      disabled={submittingComment}
                    />
                    <button
                      onClick={handleSubmitComment}
                      disabled={submittingComment || !newComment.trim()}
                      className="px-4 py-2 whitespace-nowrap bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium flex items-center justify-center gap-2"
                    >
                      {submittingComment ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                          Posting...
                        </>
                      ) : (
                        "Post"
                      )}
                    </button>
                  </div>

{/* Create Child Task Button */}
                  <button
                    onClick={handleOpenChildTaskModal}
                    className="px-4 py-2 whitespace-nowrap bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <FaPlus className="text-xs" />
                    Add Sub-Task
                  </button>
                </div>
              </div>


{/* Child Tasks Section - COMPACT */}             
 <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-1 h-4 bg-green-600 rounded"></span>
                  Sub-Tasks ({childTasks.length})
                </h3>

                <div className="space-y-2">
                  {loadingChildTasks ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">
                        Loading sub-tasks...
                      </span>
                    </div>
                  ) : childTasks.length === 0 ? (
                    <div className="text-center py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 mt-3">
                        No sub-tasks yet.
                      </p>
                    </div>
                  ) : (
                    childTasks.map((childTask) => (
                      <div
                        key={childTask.id}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {childTask.subject}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-600">
                              <span>{childTask.id}</span>
                              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                              <span>{getTeamMemberName(childTask)}</span>
                              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                              <span>
                                Due:{" "}
                                {new Date(
                                  childTask.dueDate
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                                getPriorityInfo(childTask.priority).color
                              }`}
                            >
                              {getPriorityInfo(childTask.priority).text}
                            </span>
                            {getStatusBadge(childTask.status)}
                          </div>
                        </div>
                        {childTask.description && (
                          <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded border-l-2 border-green-400 line-clamp-2">
                            {childTask.description}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Child Task Modal */}
      {showChildTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                Create Child Task
              </h2>
              <button
                onClick={handleCloseChildTaskModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Parent Task:</strong> {selectedTaskForDetail.subject}{" "}
                  (ID: {selectedTaskForDetail.id})
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To <span className="text-red-500">*</span>
                </label>
                <Select
                  value={childTaskFormData.assignedTo}
                  onChange={(selected) =>
                    handleChildTaskFormChange("assignedTo", selected)
                  }
                  options={usersList.map((user) => ({
                    value: user.id,
                    label: `${user.first_name} ${user.last_name} (${user.email})`,
                  }))}
                  placeholder="Select a team member"
                  isSearchable
                  isClearable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={childTaskFormData.subject}
                  onChange={(e) =>
                    handleChildTaskFormChange("subject", e.target.value)
                  }
                  placeholder="Enter child task subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={childTaskFormData.description}
                  onChange={(e) =>
                    handleChildTaskFormChange("description", e.target.value)
                  }
                  placeholder="Enter child task description"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-vertical"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority <span className="text-red-500">*</span>
                </label>
                <Select
                  value={childTaskFormData.priority}
                  onChange={(selected) =>
                    handleChildTaskFormChange("priority", selected)
                  }
                  options={priorityOptions}
                  placeholder="Select priority level"
                  isClearable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={childTaskFormData.dueDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleChildTaskFormChange("dueDate", e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseChildTaskModal}
                disabled={updatingTask}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChildTask}
                disabled={
                  updatingTask ||
                  !childTaskFormData.assignedTo ||
                  !childTaskFormData.subject ||
                  !childTaskFormData.priority
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {updatingTask && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {updatingTask ? "Creating..." : "Create Child Task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskManagement;
